import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleHelp, Download, LogOut, Moon, Sun, User, X } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { ModalInstalarApp } from "@/shared/components/ModalInstalarApp";
import { useFecharComEsc } from "@/shared/hooks/useFecharComEsc";
import { usePwaInstall } from "@/shared/hooks/usePwaInstall";
import { useTheme } from "@/shared/theme/ThemeProvider";

interface ModuleDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Menu de "Conta" (Model C, Sprint 3 — redesign Elos). Até a introdução da
 * barra fixa inferior (Início/Calendário/Módulos/Núcleo — ver
 * `BottomTabBar.tsx`), este drawer também listava os módulos e a gestão do
 * Núcleo; isso foi todo movido para a barra fixa, sempre visível a um toque
 * de distância (pedido do usuário: o hambúrguer não deve mais duplicar essa
 * navegação). Aqui ficou só o que a barra não cobre: perfil, Ajuda do Elos, sair, instalar o app
 * (só no navegador; some quando o Elos já está instalado) e o tema.
 */
export function ModuleDrawer({ open, onClose }: ModuleDrawerProps) {
  const { logout } = useAuth();
  useFecharComEsc(open, onClose);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [confirmandoSaida, setConfirmandoSaida] = useState(false);
  const [instrucoesAbertas, setInstrucoesAbertas] = useState(false);
  const { mostrarInstalar, instalaComUmToque, instalar } = usePwaInstall();

  function irPara(path: string) {
    navigate(path);
    onClose();
  }

  function handleInstalar() {
    // Com o aviso do navegador, a janela de instalação abre direto (precisa ser chamada dentro do
    // toque). Sem ele, mostra o passo a passo.
    if (instalaComUmToque) void instalar();
    else setInstrucoesAbertas(true);
    onClose();
  }

  async function handleSair() {
    setConfirmandoSaida(false);
    onClose();
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden={!open}
        className={`fixed inset-0 z-30 bg-black/50 transition-opacity ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Conta"
        className={`fixed bottom-0 right-0 top-0 z-40 flex w-[300px] max-w-[85vw] flex-col border-l border-border bg-surface transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-4">
          <h2 className="text-base font-bold text-text">Conta</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-text"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          <button
            type="button"
            onClick={() => irPara("/perfil")}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-text transition-colors hover:bg-surface-2"
          >
            <User size={20} className="shrink-0" />
            <span className="text-sm font-semibold">Meu perfil</span>
          </button>
          <button
            type="button"
            onClick={() => irPara("/ajuda")}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-text transition-colors hover:bg-surface-2"
          >
            <CircleHelp size={20} className="shrink-0" />
            <span className="text-sm font-semibold">Ajuda do Elos</span>
          </button>
          <button
            type="button"
            onClick={() => setConfirmandoSaida(true)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-text transition-colors hover:bg-surface-2"
          >
            <LogOut size={20} className="shrink-0" />
            <span className="text-sm font-semibold">Sair</span>
          </button>
        </div>

        <div className="flex flex-col gap-2 border-t border-border p-3">
          {mostrarInstalar && (
            <button
              type="button"
              onClick={handleInstalar}
              className="flex w-full items-center gap-3 rounded-xl border border-primary bg-primary/10 px-3 py-2.5 text-left text-text transition-colors hover:bg-primary/15"
            >
              <Download size={18} className="shrink-0 text-primary" />
              <span className="min-w-0">
                <span className="block text-sm font-bold">Instalar app</span>
                <span className="block text-xs text-muted">Abre o Elos como aplicativo</span>
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Alternar tema claro/escuro"
            className="flex w-full items-center justify-between rounded-xl border border-border bg-surface-2 px-3 py-2.5"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-text">
              {theme === "dark" ? <Moon size={18} /> : <Sun size={18} />}
              {theme === "dark" ? "Tema escuro" : "Tema claro"}
            </span>
            <span
              className={`relative h-6 w-11 rounded-full transition-colors ${
                theme === "dark" ? "bg-primary" : "bg-border"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-transform ${
                  theme === "dark" ? "left-0.5 translate-x-5" : "left-0.5"
                }`}
              />
            </span>
          </button>
        </div>
      </aside>

      <ModalInstalarApp aberto={instrucoesAbertas} onFechar={() => setInstrucoesAbertas(false)} />

      <ModalConfirmacao
        aberto={confirmandoSaida}
        titulo="Sair da conta"
        mensagem="Você precisará entrar de novo para usar o Elos."
        textoBotaoConfirmar="Sair"
        variante="padrao"
        onConfirmar={() => void handleSair()}
        onCancelar={() => setConfirmandoSaida(false)}
      />
    </>
  );
}
